<template>
    <div class="custom-tooltip" v-if="showTooltip" :style="tooltipCSS" popover="manual">
        <!-- Name and date -->
        <template v-if="isDaySpecificData(tooltipData)">
            <span v-if="isResourceTooltip(tooltipData)" class="resource-information">{{ resourceDisplayName }} - {{ humanFriendlyDate }}</span>
            <span v-else-if="isCompanyTooltip(tooltipData)" class="resource-information">{{ tooltipData.company.name }} - {{ humanFriendlyDate }}</span>

            <StampTooltip v-if="isStampData(tooltipData)" :stamp="tooltipData.stamp" :date="tooltipData.day" :shifts="shifts" :rosters="rosters" :rosterId="rosterId" />
        </template>

        <!-- Deployments -->
        <template
            v-if="deploymentsActive"
        >
            <TooltipHeader text="Diensten" />

            <div v-for="(deployment, index) in relevantDeployments" :key="index" class="deployment">
                <div v-if="shifts[deployment.shiftId]">
                    <strong>{{ shifts[deployment.shiftId].template!.code }}</strong>
                    <strong v-if="shifts[deployment.shiftId].rosterId !== rosterId">
                        ({{ getRosterLabelById(shifts[deployment.shiftId].rosterId) }})
                    </strong>:
                    <span>{{ shifts[deployment.shiftId].template!.legend }}</span>
                </div>
                <div class="comment" v-if="deployment.publicComment"><b>Opmerking:</b> {{ deployment.publicComment }}
                </div>
                <div class="tooltip-specifications">
                    <div v-for="(specification, deploymentIndex) in deployment.specifications" :key="index + 'd' + deploymentIndex">
                        {{ returnTime(specification.begin) }} - {{ returnTime(specification.end) }}:
                        {{ organisationDeploymentTypes[specification.typeId].name }}
                        /
                        {{ organisationDeploymentStates[specification.stateId].caption }}
                        <span v-if="specification.projectId">
                            -
                            {{ getProjectDescriptionByProjectId(specification.projectId) }}
                            {{ getProjectActivityByProjectIdAndActivityId(specification.projectId, specification.activityId) }}
                        </span>
                    </div>
                </div>
            </div>
        </template>

        <!-- Leave requests plus state -->
        <div
            class="leave-period"
            v-if="!isDragOverData(tooltipData) && isResourceTooltip(tooltipData) && tooltipData.resource.leavePeriodsFromForms && tooltipData.resource.leavePeriodsFromForms.length > 0"
        >
            <template v-for="(item, index) in tooltipData.resource.leavePeriodsFromForms" :key="index">
                <div v-if="dateIsEqualOrBetweenDates(item.beginDate, item.endDate)">
                    <TooltipHeader v-if="index === 0" text="Formulier" />
                    <span class="leave-period__resume">
                        Verlofaanvraag <strong :class="leavePeriodStates[item.state]">{{ item.stateDisplayText }}</strong> van {{ formatDate(item.beginDate) }} tm {{ formatDate(item.endDate) }}
                    </span>
                    <br />
                    <span class="leave-period__type card-header"><strong>Kaart</strong>: {{ item.leaveFullName }}</span>
                    <hr v-if="tooltipData.resource.leavePeriodsFromForms.length > 0 && index < tooltipData.resource.leavePeriodsFromForms.length -1" />
                </div>
            </template>
        </div>

        <!-- TODO: leave periods from roLeavePeriods should also be displayed -->

        <!-- Wish -->
        <div class="wish" v-if="isResourceTooltip(tooltipData) && (isDeploymentData(tooltipData) || isStampData(tooltipData)) && tooltipData.resource.personalPlanningWishes">
            <template v-for="(wish, index) in tooltipData.resource.personalPlanningWishes" :key="index">
                <div v-if="(wish.recursWeekly && (wish.recursOnDay % 7) === getDay(tooltipData.day)) || (wish.date && wish.date.valueOf() === tooltipData.day.valueOf())">
                    <TooltipHeader text="Wens" />
                    <strong>Opmerking</strong>: {{ wish.description }}
                    <br />
                    <span v-if="wish.doesntWantToWorkBegin && wish.doesntWantToWorkEnd" class="wish-time-details">Wil niet werken van {{ returnTime(wish.doesntWantToWorkBegin) }} tot {{ returnTime(wish.doesntWantToWorkEnd) }}</span>
                    <span v-if="wish.wantsShiftId1">Wil dienst </span>
                    <strong v-if="wish.wantsShiftId1">
                        {{ getTemplateCode(wish.wantsShiftId1) }}
                    </strong>
                    <span v-if="wish.wantsShiftId2"> of </span>
                    <strong v-if="wish.wantsShiftId2">
                        {{ getTemplateCode(wish.wantsShiftId2) }}
                    </strong>
                    <span v-if="wish.wantsShiftId3"> of </span>
                    <strong v-if="wish.wantsShiftId3">
                        {{ getTemplateCode(wish.wantsShiftId3) }}
                    </strong>
                </div>
            </template>
        </div>

        <div v-if="!isEmployed" class="warn"><b>Let op: </b> medewerker is uit dienst</div>

        <!-- availability -->
        <div class="availability" v-if="unavailabilities.length">
            <TooltipHeader text="Niet beschikbaar" />
            <div v-for="unavailability in unavailabilities" :key="unavailability.id">
                van {{ formatTimeToHourMinutes(unavailability.startTime) }} tot {{ formatTimeToHourMinutes(unavailability.endTime) }}
                <span v-if="unavailability.remarks">({{ unavailability.remarks }})</span>
                <div class="unavailability-last-edit-date">Laatst gewijzigd op: {{ formatDateTime(unavailability.updatedAt, 'onbekend') }}</div>
            </div>
        </div>

        <!-- ATW issues -->
        <template v-if="isAtwIssueData(tooltipData) && isResourceTooltip(tooltipData) && tooltipData.resource.atwIssues">
            <div class="atwIssues" v-if="tooltipData.resource.atwIssues.length > 0">
                <TooltipHeader :text="'ATW overtredingen - ' + (resourceDisplayName ?? 'Onbekend')" warning />
                <ul>
                    <li v-for="(issue, index) in tooltipData.resource.atwIssues" :key="index">{{ issue.message }}</li>
                </ul>
            </div>
        </template>

        <!-- Sick periods -->
        <div class="sickPeriod" v-for="sickPeriod in sickPeriods" :key="sickPeriod.id">
            <TooltipHeader text="Ziekteperiode" />
            <div>Van {{ formatDateHumanFriendly(sickPeriod.beginDate) }}
                <span v-if="sickPeriod.endDate">
                    tot {{ formatDateHumanFriendly(sickPeriod.endDate) }}
                </span>
                <span class="expected-recovery-date" v-else-if="sickPeriod.expectedRecoveryDate">
                    tot verwacht {{ formatDateHumanFriendly(sickPeriod.expectedRecoveryDate) }}
                </span>

            </div>
            <template v-if="sickPeriod.percentages">
                <div v-for="percentage in sickPeriod.percentages" :key="percentage.id">
                    - Vanaf {{ formatDateHumanFriendly(percentage.beginDate) }} ({{ percentage.percentage }}%)
                </div>
            </template>
        </div>

        <!-- Position Assignment Positions -->
        <div class="possitionAssignment" v-if="positionAssignmentActive && positionAssignmentGroups">
            <TooltipHeader text="Positie toewijzing" />
            <TooltipPositionAssignment
                v-for="(positionAssignmentsForPosition, positionId) of positionAssignments"
                :key="'positionId|' + positionId"
                :positionId="Number(positionId)"
                :positionDeployments="positionAssignmentsForPosition"
                :positionGroups="positionAssignmentGroups"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import StampTooltip from '@/components/Tooltip/StampTooltip.vue';
    import TooltipHeader from '@/components/Tooltip/TooltipHeader.vue';
    import TooltipPositionAssignment from '@/components/Tooltip/TooltipPositionAssignment.vue';
    import TooltipDataUtil from '@/util/TooltipDataUtil';
    import Dates from '@cquential/date-utils';
    import {Deployment, PositionAssignmentSetGroupPositionDeployment, SickPeriod, SickPercentage, Project, Activity, OrganisationDeploymentState, DeploymentType, Shift, PositionAssignmentSetGroup, Unavailability} from '@cquential/api';
    import {RosterSelectOption} from '@/types/types';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import {Component, Prop, Vue} from 'vue-facing-decorator';
    import {CSSProperties} from 'vue';
    import {ToolTipData, BasicTooltipData, ToolTipSubject, DragOverTooltipData, DeploymentToolTipData, StampToolTipData, AtwIssueToolTipData, DaySpecificTooltipData, ToolTipSubjectResource, ToolTipDataType, ToolTipSubjectCompany} from '@/types';

    @Component({
        components: {
            StampTooltip,
            TooltipHeader,
            TooltipPositionAssignment,
        },
    })
    export default class Tooltip extends Vue {
        @Prop({required: true}) public rosters!: RosterSelectOption[];
        @Prop({required: true}) public rosterId!: number;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public organisationDeploymentTypes!: Record<number, DeploymentType>;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({type: Boolean}) public allowVisibility!: boolean;
        @Prop() public projects?: Project[];
        @Prop() public positionAssignmentGroups?: PositionAssignmentSetGroup[];

        // Position of tooltip
        // Colors for the situations in leave periods
        public leavePeriodStates: Record<string, string> = {
            accepted: 'accepted',
            withdrawal_requested: 'accepted withdrawalRequested',
            withdrawaldev: 'accepted withdrawalRequested',
            progressdev: 'progressdev',
            denied: 'denied',
            submitted: 'submitted',
        };

        public tooltipData: ToolTipData | null = null;
        public tooltipCSS: CSSProperties = {
            top: '0px',
            left: '0px',
        };

        private static readonly TOOLTIP_WIDTH: number = 300;
        private static instance: Tooltip | null;

        public mounted(): void {
            Tooltip.instance = this;
        }

        public beforeUnmount(): void {
            Tooltip.instance = null;
        }

        // Show or hide tooltip if at least one of the methods in the array returns true
        public get showTooltip(): boolean {
            return this.allowVisibility
                && (
                    this.deploymentsActive
                    || this.leavePeriodActive
                    || this.wishActive
                    || this.unavailabilitiesActive
                    || this.atwIssuesActive
                    || this.stampActive
                    || this.sickPeriodsActive
                    || this.positionAssignmentActive
                    || this.isDragOverEvent
                );
        }

        public get isDragOverEvent(): boolean {
            return this.tooltipData !== null && TooltipDataUtil.isDragOverData(this.tooltipData);
        }

        public get hasValidResource(): boolean {
            return TooltipDataUtil.isResourceTooltip(this.tooltipData) && !!this.tooltipData.resource.id;
        }

        // this should be a getter, but type predicates are only allowed on normal functions
        public isDaySpecificData(tooltipData: ToolTipData | null): tooltipData is ToolTipData & ToolTipSubject & DaySpecificTooltipData {
            return TooltipDataUtil.isDaySpecificData(tooltipData);
        }

        // this should be a getter, but type predicates are only allowed on normal functions
        public isResourceTooltip(tooltipData: ToolTipData | null): tooltipData is BasicTooltipData & ToolTipSubjectResource & ToolTipDataType {
            return TooltipDataUtil.isResourceTooltip(tooltipData);
        }

        // this should be a getter, but type predicates are only allowed on normal functions
        public isCompanyTooltip(tooltipData: ToolTipData | null): tooltipData is BasicTooltipData & ToolTipSubjectCompany & ToolTipDataType {
            return TooltipDataUtil.isCompanyTooltip(tooltipData);
        }

        // this should be a getter, but type predicates are only allowed on normal functions
        public isStampData(tooltipData: ToolTipData | null): tooltipData is BasicTooltipData & ToolTipSubject & StampToolTipData {
            return TooltipDataUtil.isStampData(tooltipData);
        }

        // this should be a getter, but type predicates are only allowed on normal functions
        public isDragOverData(tooltipData: ToolTipData | null): tooltipData is BasicTooltipData & ToolTipSubject & DragOverTooltipData {
            return TooltipDataUtil.isDragOverData(tooltipData);
        }

        // this should be a getter, but type predicates are only allowed on normal functions
        public isDeploymentData(tooltipData: ToolTipData | null): tooltipData is BasicTooltipData & ToolTipSubject & DeploymentToolTipData {
            return TooltipDataUtil.isDeploymentData(tooltipData);
        }

        // this should be a getter, but type predicates are only allowed on normal functions
        public isAtwIssueData(tooltipData: ToolTipData | null): tooltipData is BasicTooltipData & ToolTipSubject & AtwIssueToolTipData {
            return TooltipDataUtil.isAtwIssueData(tooltipData);
        }

        public get resourceDisplayName(): string {
            if (this.tooltipData && TooltipDataUtil.isResourceTooltip(this.tooltipData)) {
                return ResourceUtil.displayName(this.tooltipData.resource);
            }

            return '';
        }

        // Check if there are deployments for props.date
        public get deploymentsActive(): boolean {
            return this.tooltipData !== null
                && TooltipDataUtil.isDeploymentData(this.tooltipData)
                && this.tooltipData.deployments.length > 0;
        }

        public get sickPeriodsActive(): boolean {
            return this.sickPeriods.length > 0;
        }

        // Check if there is a leavePeriod (request) active for date.value
        public get leavePeriodActive(): boolean {
            if (this.hasValidResource
                && TooltipDataUtil.isResourceTooltip(this.tooltipData)
                && TooltipDataUtil.isDaySpecificData(this.tooltipData)
            ) {
                if (this.tooltipData.resource.leavePeriodsFromForms && this.tooltipData.resource.leavePeriodsFromForms.length > 0) {
                    for (const leave of this.tooltipData.resource.leavePeriodsFromForms) {
                        if (this.dateIsEqualOrBetweenDates(leave.beginDate, leave.endDate)) {
                            return true;
                        }
                    }
                }
            }

            return false;
        }

        // Check if there is a wish active for date.value
        public get wishActive(): boolean {
            if (this.hasValidResource && TooltipDataUtil.isResourceTooltip(this.tooltipData) && TooltipDataUtil.isDeploymentData(this.tooltipData)) {
                if (this.tooltipData.resource.personalPlanningWishes && this.tooltipData.resource.personalPlanningWishes.length > 0) {
                    for (const wish of this.tooltipData.resource.personalPlanningWishes) {
                        if ((wish.recursWeekly && (wish.recursOnDay % 7) === Dates.Utils.getDay(this.tooltipData.day)) || (wish.date && wish.date.valueOf() === this.tooltipData.day.valueOf())) {
                            return true;
                        }
                    }
                }
            }

            return false;
        }

        // Check if there one or more ATW issues in place
        public get atwIssuesActive(): boolean {
            return this.hasValidResource && TooltipDataUtil.isResourceTooltip(this.tooltipData) && TooltipDataUtil.isAtwIssueData(this.tooltipData) && !!this.tooltipData.resource.atwIssues && this.tooltipData.resource.atwIssues.length > 0;
        }

        // Check if there a stamp active
        public get stampActive(): boolean {
            return this.hasValidResource && TooltipDataUtil.isStampData(this.tooltipData) && !!this.tooltipData.stamp;
        }

        public get positionAssignmentActive(): boolean {
            return this.hasValidResource && this.deploymentsActive
                && TooltipDataUtil.isDeploymentData(this.tooltipData) && this.tooltipData.deployments.some((deployment: Deployment): boolean => {
                    return !!deployment.positionAssignmentSetGroupPositionDeployments && deployment.positionAssignmentSetGroupPositionDeployments.length > 0;
                });
        }

        public get unavailabilitiesActive(): boolean {
            return this.unavailabilities.length > 0;
        }

        public get isEmployed(): boolean {
            return !this.tooltipData || !TooltipDataUtil.isDaySpecificData(this.tooltipData) || !TooltipDataUtil.isResourceTooltip(this.tooltipData) || (this.tooltipData.resource.beginDate <= this.tooltipData.day && this.tooltipData.resource.endDate >= this.tooltipData.day);
        }

        public get relevantDeployments(): Deployment[] {
            if (this.tooltipData && TooltipDataUtil.isDeploymentData(this.tooltipData)) {
                return [...this.tooltipData.deployments].sort((a: Deployment, b: Deployment): number => {
                    if (!a.specifications || a.specifications.length === 0) {
                        return 1;
                    }
                    if (!b.specifications || b.specifications.length === 0) {
                        return -1;
                    }

                    return a.specifications![0].begin.valueOf() - b.specifications![0].begin.valueOf();
                });
            }

            return [];
        }

        public get positionAssignments(): Record<number, PositionAssignmentSetGroupPositionDeployment[]> {
            const positionDeploymentsByPosition: Record<number, PositionAssignmentSetGroupPositionDeployment[]> = {};
            for (const positionDeployment of this.relevantDeployments.map((deployment: Deployment): PositionAssignmentSetGroupPositionDeployment[] => {
                return deployment.positionAssignmentSetGroupPositionDeployments ?? [];
            }).flat()) {
                if (!positionDeploymentsByPosition[positionDeployment.positionId]) {
                    positionDeploymentsByPosition[positionDeployment.positionId] = [];
                }

                positionDeploymentsByPosition[positionDeployment.positionId].push(positionDeployment);
            }

            return positionDeploymentsByPosition;
        }

        public get unavailabilities(): Unavailability[] {
            if (TooltipDataUtil.isDaySpecificData(this.tooltipData)
                && TooltipDataUtil.isResourceTooltip(this.tooltipData)
                && this.tooltipData.resource.unavailabilities
            ) {
                return this.tooltipData.resource.unavailabilities[Dates.Formatter.formatDateISO(this.tooltipData.day)] ?? [];
            }

            return [];
        }

        public get humanFriendlyDate(): string {
            return (this.tooltipData && 'day' in this.tooltipData) ? Dates.Formatter.formatDateHumanFriendly(this.tooltipData.day) : '';
        }

        public get sickPeriods(): SickPeriod[] {
            if (this.tooltipData && TooltipDataUtil.isResourceTooltip(this.tooltipData) && TooltipDataUtil.isDeploymentData(this.tooltipData) && this.tooltipData.resource.sickPeriods) {
                const day: Date = this.tooltipData.day;
                const sickPeriods: SickPeriod[] = this.tooltipData.resource.sickPeriods.filter((sickPeriod: SickPeriod): boolean => {
                    return day >= sickPeriod.beginDate && (sickPeriod.endDate === undefined || day < sickPeriod.endDate);
                });

                for (const sickPeriod of sickPeriods) {
                    sickPeriod.percentages!.sort((a: SickPercentage, b: SickPercentage): number => {
                        return a.beginDate.valueOf() - b.beginDate.valueOf();
                    });
                }

                return sickPeriods;
            }

            return [];
        }

        private resourceIdsAreTheSame(a: ToolTipData, b: ToolTipData): boolean {
            if (a.resource && b.resource) {
                return a.resource.id === b.resource.id;
            }

            return false;
        }

        private companyIdsAreTheSame(a: ToolTipData, b: ToolTipData): boolean {
            if (a.company && b.company) {
                return a.company.id === b.company.id;
            }

            return false;
        }

        public tooltipIsAlreadyShownForData(data?: ToolTipData): boolean {
            return (
                this.tooltipData && data
                && TooltipDataUtil.isDaySpecificData(this.tooltipData) && TooltipDataUtil.isDaySpecificData(data)
                && this.tooltipData.day.getTime() === data.day.getTime()
                && (this.resourceIdsAreTheSame(this.tooltipData, data) || this.companyIdsAreTheSame(this.tooltipData, data))
            ) || false;
        }

        // This is not ideal but the way tooltip.value was built does not allow anything better
        public getRosterLabelById(rosterId: number): string {
            return this.rosters.find((rosterOption: RosterSelectOption): boolean => rosterOption.data.id === rosterId)?.label ?? 'Vervallen rooster';
        }

        // Method that returns time in a certain format
        public returnTime(date: Date): string {
            let addition: string = '';
            if (this.tooltipData && this.tooltipData.type === 'deployment' && Dates.Formatter.formatDateComputerFriendly(date) > Dates.Formatter.formatDateComputerFriendly(this.tooltipData.day)) {
                addition += ' (+1)';
            }

            return Dates.Formatter.formatTime(date) + addition;
        }

        public formatTimeToHourMinutes(time: string): string {
            const minutes: number = Math.round(Dates.Time.parse(time) / 60);
            const hours: number = Math.floor(minutes / 60);

            return String(hours).padStart(2, '0') + ':' + String(minutes % 60).padStart(2, '0');
        }

        public formatDate(date: Date): string {
            return Dates.Formatter.formatDate(date);
        }

        public formatDateTime(date: Date | undefined, noDateString: string = ''): string {
            if (!date) {
                return noDateString;
            }

            return Dates.Formatter.formatDateTime(date);
        }

        public formatDateHumanFriendly(date: Date): string {
            return Dates.Formatter.formatDateHumanFriendly(date);
        }

        public getDay(date: Date): number {
            return Dates.Utils.getDay(date);
        }

        public getTemplateCode(shiftId: number): string {
            return this.shifts[shiftId]?.template!.code ?? '';
        }

        // Method that gives back a boolean if day is between or equal to beginDate and endDate
        public dateIsEqualOrBetweenDates(beginDate: Date, endDate: Date): boolean {
            if (this.tooltipData && TooltipDataUtil.isDaySpecificData(this.tooltipData)) {
                return Dates.compare(this.tooltipData.day).isBetweenDatesInclusive(beginDate, endDate);
            }

            return false;
        }

        private onChangeValue(): void {
            if (this.tooltipData && this.tooltipData.event && this.tooltipData.event.target) {
                this.$nextTick((): void => {
                    if (this.$el instanceof HTMLDivElement) {
                        this.$el.showPopover();
                    }
                });
                const cell: HTMLElement = this.tooltipData.event.target as HTMLElement;
                const cellBoundingBox: DOMRect = cell.getBoundingClientRect();

                if (this.tooltipData.type === 'atwIssues') {
                    this.tooltipCSS = {
                        top: (cellBoundingBox.top) + 'px',
                        left: (cellBoundingBox.left + (cell.clientWidth + 10)) + 'px',
                    };
                } else {
                    const left: number = cellBoundingBox.x < (document.body.clientWidth / 2)
                        ? cellBoundingBox.left
                        : cellBoundingBox.left - Tooltip.TOOLTIP_WIDTH + cell.clientWidth;

                    this.tooltipCSS = {
                        top: (cellBoundingBox.top + cell.clientHeight + 10) + 'px',
                        left: left + 'px',
                        width: Tooltip.TOOLTIP_WIDTH + 'px',
                    };
                }
            }
        }

        public getProjectDescriptionByProjectId(projectId: number): string {
            const project: Project | undefined = this.projects?.find((project: Project): boolean => project.id === projectId);

            if (project) {
                return 'P: ' + project.id + ' - ' + project.description;
            }

            return '';
        }

        public getProjectActivityByProjectIdAndActivityId(projectId: number, activityId: number | null): string {
            const activity: Activity | undefined = activityId ? this.projects?.find((project: Project): boolean => project.id === projectId)?.activities?.find((activity: Activity): boolean => activity.id === activityId) : undefined;

            if (activity) {
                return ' / A: ' + activity.id + ' - ' + activity.description;
            }

            return '';
        }

        public static setTooltip(data: ToolTipData): void {
            this.instance!.tooltipData = data;
            this.instance!.onChangeValue();
        }

        public static closeTooltip(): void {
            this.instance!.tooltipData = null;
            this.instance!.onChangeValue();
        }

        public static tooltipIsAlreadyShownForData(data?: ToolTipData): boolean {
            return this.instance!.tooltipIsAlreadyShownForData(data);
        }
    }

</script>

<style lang="scss" scoped>
    @use '@/styles/tooltip';

    .custom-tooltip {
        margin: 0;
        padding: 8px;
        background: var(--modal-background);
        color: var(--theme-text-color);
        z-index: 2100;

        .resource-information, .card-header, .deployment {
            display: block;
            margin-bottom: 8px;
        }

        .resource-information {
            font-weight: 700;
        }

        .wish-time-details {
            display: block;
            margin: 4px 0 8px;
        }

        .unavailability-last-edit-date {
            font-style: italic;
        }
    }

    .expected-recovery-date {
        font-style: italic;
    }

    .tooltip-specifications, .comment {
        padding-left: 3px;
        border-left: 2px solid #ccc;
        margin-left: 3px;
    }

    .comment {
        display: var(--compact-show-inline-block);
    }

    .warn {
        color: orangered;

        b {
            color: red;
        }
    }
</style>
